"use client";

import * as React from "react";
import Input from "./Input";
import { Small } from "../typography";
import { formatDateValue, validateCalendarDate } from "./dateInputUtils";

type Props = {
  /**
   * The masked MM/DD/YYYY text the form holds (may be partial while typing).
   * The parent owns this string and converts/validates it with the helpers in
   * `dateInputUtils` (`displayToIso` / `validateCalendarDate`) — so what's
   * stored always matches what's shown, and an incomplete entry simply fails
   * submit validation instead of silently saving a stale or empty value.
   */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Parent-supplied error (e.g. cross-field validation); shown below the input. */
  error?: string;
  className?: string;
  id?: string;
  ariaLabel?: string;
  /**
   * When true, reject dates before today (today itself is allowed). Use for
   * expiration fields where a past date is meaningless. The parent must apply
   * the same option in its own submit gate: `validateCalendarDate(value, { notInPast: true })`.
   */
  notInPast?: boolean;
};

/**
 * Masked MM/DD/YYYY text input — a typed alternative to the calendar picker for
 * dates that may be far in the future (e.g. credit/certificate expirations).
 * Fully controlled on the display text, mirroring DateOfBirthInput.
 */
const DateInput: React.FC<Props> = ({
  value,
  onChange,
  placeholder = "MM/DD/YYYY",
  error,
  className,
  id,
  ariaLabel,
  notInPast,
}) => {
  const [touched, setTouched] = React.useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(formatDateValue(e.target.value));
  };

  // Surface a format error once the field has been blurred and holds an
  // incomplete/invalid entry. (Parent `error` always wins.)
  const localError =
    touched && value
      ? validateCalendarDate(value, { notInPast }) ?? undefined
      : undefined;
  const shownError = error ?? localError;

  return (
    <div className="flex flex-col gap-2">
      <Input
        id={id}
        value={value}
        onChange={handleChange}
        onBlur={() => setTouched(true)}
        placeholder={placeholder}
        maxLength={10}
        inputMode="numeric"
        isError={!!shownError}
        aria-label={ariaLabel}
        className={className}
      />
      {shownError && (
        <Small className="ps-2 font-medium text-destructive">
          {shownError}
        </Small>
      )}
    </div>
  );
};

export default DateInput;
